import { useRef, useEffect } from 'react';
import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useConfiguratorStore } from '@store/useConfiguratorStore';
import { getProductById } from '@/config/products';
import PrintAreaTexture from '../Logo/PrintAreaTexture';
import TShirtPlaceholder from './TShirtPlaceholder';
import ModelInteraction from '@components/Scene/ModelInteraction';
import { diagnoseUVMapping, applyCylindricalUVs, normalizeUVs } from '@/utils/uvDiagnostics';

// Modular Strategy Imports
import { createGrainTexture } from '@/utils/model-strategies/textureGenerator';
import { processNylonLanyardGeometry, processSquarePenGeometry } from '@/utils/model-strategies/geometryModifiers';
import {
  applyGlassMaterial,
  applyPenMaterials,
  applyCapMaterial,
  applyJarMaterials,
  applyDefaultProperties
} from '@/utils/model-strategies/materialApplicator';

export default function ModelLoader() {
  const groupRef = useRef<THREE.Group>(null);
  const { glbUrl, currentModel, setModelLoaded, setModelParts, setPrintAreas, colorCustomizations, printAreas, showPrintAreaBorders, setTextInstances, setLogoInstances, activePrintAreaId } = useConfiguratorStore();

  // Generate grain texture once
  const grainTexture = useRef<THREE.Texture | null>(null);
  useEffect(() => {
    grainTexture.current = createGrainTexture();

    return () => {
      if (grainTexture.current) {
        grainTexture.current.dispose();
        grainTexture.current = null;
      }
    };
  }, []);


  // Get product config
  const product = currentModel ? getProductById(currentModel) : undefined;

  // Material caching for better undo/redo performance
  const originalMaterialsRef = useRef<Map<THREE.Mesh, THREE.Material | THREE.Material[]>>(new Map());
  const materialCacheRef = useRef<Map<string, THREE.MeshStandardMaterial>>(new Map());

  // Load GLB using useGLTF hook (models already preloaded by App)
  const gltf = glbUrl ? useGLTF(glbUrl) : null;


  // Process loaded GLTF
  useEffect(() => {
    // Show loading spinner immediately when URL changes
    if (glbUrl) {
      // Handled by RightPanel now to catch the suspense phase
    }

    if (!gltf || !gltf.scene) {
      // No GLB loaded - use placeholder
      if (!glbUrl) {
        setModelLoaded(true);
        setModelParts([]);
        useConfiguratorStore.getState().setLoading(false);
      }
      return;
    }

    // --- CRITICAL FIX: Clone Scene & Geometry ---
    // We MUST clone the scene to prevent shared state pollution between components/reloads.
    const sceneToProcess = gltf.scene.clone(true);

    const parts: any[] = [];
    const printAreas: any[] = [];

    // --- GEOMETRY PRE-PASSES (Modularized) ---
    // 1. Nylon Lanyard: Create merged "All Faces"
    const nylonLanyardSharedTexture = processNylonLanyardGeometry(sceneToProcess, product, printAreas);

    // 2. Square Pen: Split Body into faces and create wrapper
    processSquarePenGeometry(sceneToProcess, product, printAreas);

    // --- MAIN TRAVERSAL ---
    sceneToProcess.traverse((child: any) => {
      if (!child.isMesh) return;
      if (child.userData.ignorePart) return; // Skip ignored parts

      const name = child.name;
      const nameLower = name.toLowerCase();

      // Skip the All Faces wrapper - already set up by processSquarePenGeometry
      if (name === "PrintArea_All_Faces") {
        return;
      }

      // Check if this is a print area
      if (
        nameLower.includes('printarea') ||
        nameLower.includes('print_area') ||
        nameLower.includes('imprint')
      ) {
        // Skip All Faces wrapper - it's already set up by processSquarePenGeometry
        if (child.userData.isAllFacesWrapper) {
          return;
        }

        // SKIP curved "T" print area for dye-sublimated lanyard
        if (product?.id === 'dye-sublimated-lanyard-with-metal-crimp' && nameLower.includes('printarea_t')) {
          child.visible = false;
          return;
        }

        // Extract clean name
        const cleanName = name
          .replace(/PrintArea_/i, '')
          .replace(/PrintArea/i, '')
          .trim();

        // SPECIAL FIX: Tumbler model has broken/mirrored UVs
        if (product?.materialConfig?.forceCylindricalUVs) {
          child.geometry = child.geometry.clone();
          // Apply 0.5 offset (180 deg) for Tumbler to move seam to back
          const offset = (product.id === 'tumbler') ? 0.8 : 0.0;
          applyCylindricalUVs(child.geometry, 'y', offset);
        }

        // SPECIAL FIX: Force UV Normalization
        if (product?.materialConfig?.forceNormalizeUVs) {
          child.geometry = child.geometry.clone();
          normalizeUVs(child.geometry);
        }

        // Extract print area info
        const box = new THREE.Box3().setFromObject(child);
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        box.getCenter(center);
        box.getSize(size);

        const geometry = child.geometry;
        let normal = new THREE.Vector3(0, 0, 1);

        if (geometry) {
          const normalAttribute = geometry.getAttribute('normal');
          if (normalAttribute && normalAttribute.count > 0) {
            normal.set(
              normalAttribute.getX(0),
              normalAttribute.getY(0),
              normalAttribute.getZ(0)
            );
            normal.applyQuaternion(child.quaternion);
            normal.normalize();
          }
        }

        let physicalWidth = size.x;
        let physicalHeight = size.y;
        
        // Determine true physical width/height based on face normal vector
        const absX = Math.abs(normal.x);
        const absY = Math.abs(normal.y);
        const absZ = Math.abs(normal.z);
        
        if (absY > absX && absY > absZ) {
          // Top/Bottom face (normal is mostly Y) -> Width is X, Height is Z
          physicalWidth = size.x;
          physicalHeight = size.z;
        } else {
          // Side face -> Height is always Y
          physicalHeight = size.y;
          // If normal is mostly X, the face plane is parallel to Z axis -> Width is Z
          if (absX > absZ) {
            physicalWidth = size.z;
          } else {
            physicalWidth = size.x;
          }
        }

        // Extract UV bounds
        const uvReport = diagnoseUVMapping(child as THREE.Mesh);

        let uvBounds = (uvReport.hasUVs && uvReport.uvRange) ? {
          minU: uvReport.uvRange.minU,
          maxU: uvReport.uvRange.maxU,
          minV: uvReport.uvRange.minV,
          maxV: uvReport.uvRange.maxV,
          needsFlipY: uvReport.needsFlipY,
        } : undefined;

        if (uvBounds) {
          const uRange = uvBounds.maxU - uvBounds.minU;
          const vRange = uvBounds.maxV - uvBounds.minV;

          if (uRange > 10.0 || vRange > 10.0 || uRange < 0.0001) {
            uvBounds = {
              minU: 0, maxU: 1, minV: 0, maxV: 1, needsFlipY: false
            };
          }
        }

        const printAreaId = (cleanName || name).toLowerCase().replace(/\s+/g, '-') || `print-area-${printAreas.length}`;

        // CAPTURE BORROWED TEXTURE
        let borrowedDiffuseTexture: THREE.Texture | null = null;

        if (product?.id === 'nylon-lanyard' && nylonLanyardSharedTexture) {
          borrowedDiffuseTexture = nylonLanyardSharedTexture;
        } else if (child.material) {
          const mat = child.material as THREE.MeshStandardMaterial;
          if (!mat.map) {
            sceneToProcess.traverse((sibling: any) => {
              if (borrowedDiffuseTexture) return;
              if (sibling.isMesh && sibling !== child) {
                if (sibling.name.toLowerCase().includes('printarea')) return;
                const materials = Array.isArray(sibling.material) ? sibling.material : [sibling.material];
                for (const mat of materials) {
                  if (mat instanceof THREE.MeshStandardMaterial && mat.map) {
                    borrowedDiffuseTexture = mat.map;
                    break;
                  }
                }
              }
            });
          }
        }

        // UV INVERSION BAKING
        let effectiveInvertY = product?.materialConfig?.invertY;
        if (uvBounds && effectiveInvertY) {
          const oldMin = uvBounds.minV;
          const oldMax = uvBounds.maxV;
          uvBounds.minV = 1 - oldMax;
          uvBounds.maxV = 1 - oldMin;
          uvBounds.needsFlipY = !uvBounds.needsFlipY;
        }

        let useTextureFlipX = product?.materialConfig?.textureFlipX;

        printAreas.push({
          id: printAreaId,
          name: cleanName || name,
          position: center.toArray(),
          bounds: { width: physicalWidth, height: physicalHeight },
          normal: normal.toArray(),
          meshRef: child as THREE.Mesh,
          parentMesh: sceneToProcess,
          uvBounds,
          borrowedDiffuseTexture: borrowedDiffuseTexture || undefined,
          invertX: product?.materialConfig?.invertX,
          invertY: product?.materialConfig?.invertY ?? false,
          textureFlip: product?.materialConfig?.textureFlip,
          textureFlipX: useTextureFlipX,
        });

        // Outline - for square-pen faces, create but start hidden (only active face shows)
        const isSquarePenFace = product?.id === 'square-pen' && nameLower.includes('printarea_face_');
        const edges = new THREE.EdgesGeometry(child.geometry);
        const outline = new THREE.LineSegments(
          edges,
          new THREE.LineBasicMaterial({ color: 0x4da6ff, linewidth: 2, transparent: true, opacity: 0.7, depthTest: true })
        );
        outline.renderOrder = 1000;
        outline.name = 'printAreaOutline';
        outline.visible = isSquarePenFace ? false : true; // Start hidden for square-pen faces
        if (isSquarePenFace) {
          outline.userData.squarePenFaceId = printAreaId; // Tag with this face's ID for selective show
        }
        child.add(outline);

        child.visible = true;
        child.userData.isPrintArea = true;

        if (child.material) {
          if (!Array.isArray(child.material)) {
            child.material = child.material.clone();
          }
          const mat = child.material as THREE.MeshStandardMaterial;
          mat.transparent = true;
          mat.opacity = 0;
          mat.color.setHex(0xffffff);
          mat.side = THREE.DoubleSide;
          mat.depthWrite = false;
          mat.polygonOffset = true;
          mat.polygonOffsetFactor = -10;
          mat.polygonOffsetUnits = -10;

          if (product?.id?.includes('lanyard')) {
            mat.polygonOffsetFactor = -20;
            mat.polygonOffsetUnits = -20;
            child.renderOrder = 9999;
            child.scale.multiplyScalar(1.005);
          }
          child.renderOrder = 2000;
        }
      } else {
        // Regular model part
        parts.push({
          name: child.name,
          visible: true,
        });
      }
    });



    setModelParts(parts);
    setPrintAreas(printAreas);
    setModelLoaded(true);

    setTimeout(() => {
      useConfiguratorStore.getState().setLoading(false);
    }, 500);

    // Add model to scene with auto-scaling
    if (groupRef.current) {
      // Clean up previous model
      const disposeObject = (obj: THREE.Object3D) => {
        if (obj instanceof THREE.Mesh) {
          if (obj.geometry) obj.geometry.dispose();
          if (obj.material) {
            if (Array.isArray(obj.material)) {
              obj.material.forEach(m => disposeMaterial(m));
            } else {
              disposeMaterial(obj.material);
            }
          }
        }
      };

      const disposeMaterial = (material: THREE.Material) => {
        Object.keys(material).forEach(key => {
          const value = (material as any)[key];
          if (value && typeof value === 'object' && 'minFilter' in value) {
            value.dispose();
          }
        });
        material.dispose();
      };

      while (groupRef.current.children.length > 0) {
        const child = groupRef.current.children[0];
        child.traverse(disposeObject);
        groupRef.current.remove(child);
      }

      const sceneToAdd = sceneToProcess;
      sceneToAdd.scale.set(1, 1, 1);
      sceneToAdd.position.set(0, 0, 0);
      sceneToAdd.rotation.set(0, 0, 0);

      const box = new THREE.Box3().setFromObject(sceneToAdd);
      const size = new THREE.Vector3();
      const center = new THREE.Vector3();
      box.getSize(size);
      box.getCenter(center);

      const maxDim = Math.max(size.x, size.y, size.z);
      const targetSize = product?.materialConfig?.targetScale || 1.7;
      const scaleFactor = targetSize / maxDim;

      const wrapper = new THREE.Group();
      wrapper.name = 'ModelWrapper';
      sceneToAdd.position.set(-center.x, -center.y, -center.z);
      wrapper.add(sceneToAdd);
      wrapper.scale.setScalar(scaleFactor);

      if (product?.materialConfig?.initialRotationX) wrapper.rotation.x = product.materialConfig.initialRotationX;
      if (product?.materialConfig?.initialRotationY) wrapper.rotation.y = product.materialConfig.initialRotationY;
      if (product?.materialConfig?.initialRotationZ) wrapper.rotation.z = product.materialConfig.initialRotationZ;

      // --- APPLY MODULAR MATERIAL STRATEGIES ---
      sceneToAdd.traverse((child) => {
        if (child instanceof THREE.Mesh && (!child.userData.isPrintArea || (currentModel === 'square-pen' && child.name.includes('Face') && !child.name.includes('All_Faces')))) {
          applyDefaultProperties(child);
          applyGlassMaterial(child, product);
          applyPenMaterials(child, product, glbUrl);
          applyCapMaterial(child, product);
          applyJarMaterials(child, product, grainTexture.current);
        }
      });

      groupRef.current.add(wrapper);
    }
  }, [gltf, glbUrl, setModelParts, setPrintAreas, setModelLoaded, product]);

  // Toggle print area border visibility
  useEffect(() => {
    if (!groupRef.current) return;
    groupRef.current.traverse((child) => {
      if (child.name === 'printAreaOutline') {
        if (child.userData.squarePenFaceId) {
          // Square pen faces: show only the active/selected face's outline
          child.visible = showPrintAreaBorders && child.userData.squarePenFaceId === activePrintAreaId;
        } else {
          child.visible = showPrintAreaBorders;
        }
      }
    });
  }, [showPrintAreaBorders, activePrintAreaId]);

  // Clear material caches
  useEffect(() => {
    originalMaterialsRef.current.clear();
    materialCacheRef.current.clear();
    setTextInstances([]);
    setLogoInstances([]);
  }, [glbUrl, setTextInstances, setLogoInstances]);

  // Wireframe Mode Effect
  const { isWireframeMode } = useConfiguratorStore();
  const wireframeMaterialRef = useRef<THREE.MeshBasicMaterial>(new THREE.MeshBasicMaterial({ color: new THREE.Color(1.5, 1.5, 1.5) }));

  useEffect(() => {
    if (!groupRef.current) return;

    // Cleanup helper: Remove OLD outlines if parameters change
    const cleanupOutlines = () => {
      groupRef.current?.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          // Find ALL outline meshes (could be multiple per child now)
          const outlines = child.children.filter(c => c.name.startsWith('globalWireframeOutline'));
          outlines.forEach(existing => {
            child.remove(existing);
            if (existing instanceof THREE.Mesh || existing instanceof THREE.LineSegments) {
              existing.geometry.dispose();
              if (Array.isArray(existing.material)) {
                existing.material.forEach(m => m.dispose());
              } else {
                existing.material.dispose();
              }
            }
          });
        }
      });
    };

    // Force cleanup before applying new ones to ensure updates
    if (isWireframeMode) {
      cleanupOutlines();
    }

    groupRef.current.traverse((child) => {
      // CRITICAL FIX: Skip our own outline meshes to prevent recursion
      if (child.name.startsWith('globalWireframeOutline')) return;

      if (child instanceof THREE.Mesh && !child.userData.isPrintArea) {

        // Find existing outlines
        const outlines = child.children.filter(c => c.name.startsWith('globalWireframeOutline'));

        if (isWireframeMode) {
          if (outlines.length === 0) {
            // --- INVERTED HULL OR EDGES ---

            const nameLower = child.name.trim().toLowerCase();

            // Strict "Body" check to avoid false positives (like Plunger/Button)
            const isBody = nameLower === 'body' || nameLower === 'barrel';

            const isGrip = nameLower.includes('grip');
            const isTip = nameLower.includes('tip');

            if (isBody) {
              // --- DASHED EDGES (Body Only) ---
              // Use EdgesGeometry with LineDashedMaterial for crisp, vector-like dashes

              // High threshold (85 deg) to remove internal diagonal shading lines.
              const edges = new THREE.EdgesGeometry(child.geometry, 85);
              const dashMat = new THREE.LineDashedMaterial({
                color: 0x000000,
                linewidth: 5,
                scale: 1,
                dashSize: 0.01, // Small crisp dashes
                gapSize: 0.02   // Clear gaps
              });

              const outlineDash = new THREE.LineSegments(edges, dashMat);
              outlineDash.computeLineDistances(); // REQUIRED for dashes to appear
              outlineDash.name = 'globalWireframeOutline_Dash';

              child.add(outlineDash);

            } else if (isTip) {
              // --- CLEAN TIP (Attempt to remove flat cap line) ---
              // Extremely High Threshold (150 deg) to ignore almost all edges except the most extreme turns.
              // This aims to hide the cap/point geometry lines entirely for a clean look.
              const edges = new THREE.EdgesGeometry(child.geometry, 150);
              const lineMat = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 4 });
              const outlineEdge = new THREE.LineSegments(edges, lineMat);
              outlineEdge.name = 'globalWireframeOutline_Edge';
              child.add(outlineEdge);

            } else {
              // --- STANDARD SOLID EDGES (Grip, Clip, etc.) ---
              // Use a more aggressive threshold (89) to remove internal subdivision noise 
              // from curved/tapered parts like the Grip.
              const edges = new THREE.EdgesGeometry(child.geometry, 89);
              const lineMat = new THREE.LineBasicMaterial({ color: 0x000000, linewidth: 4 });
              const outlineEdge = new THREE.LineSegments(edges, lineMat);
              outlineEdge.name = 'globalWireframeOutline_Edge';
              child.add(outlineEdge);

              const isClip = nameLower.includes('clip');

              // --- BOLDER CLIP: Double-Edge Strategy ---
              // For thin parts like the Clip, using a solid hull mesh (MeshBasicMaterial)
              // causes "black filler" issues. Instead, we add a second line outline
              // with a tiny offset (1.005) to create a bold "thick stroke" effect.
              if (isClip) {
                const outlineEdgeBold = new THREE.LineSegments(edges, lineMat);
                outlineEdgeBold.name = 'globalWireframeOutline_Edge_Bold';
                outlineEdgeBold.scale.setScalar(1.005);
                child.add(outlineEdgeBold);
              }

              // --- SPECIAL: SOLID HULL FOR GRIP 1 (Thick Border) ---
              // ONLY Apply to Grip 1 (Thick part can handle the solid hull backface)
              const isBoldPart = (isGrip && !nameLower.includes('2'));

              if (isBoldPart) {
                const solidHullGeom = child.geometry.clone();
                const solidHullMat = new THREE.MeshBasicMaterial({
                  color: 0x000000,
                  side: THREE.BackSide
                });
                const solidHullMesh = new THREE.Mesh(solidHullGeom, solidHullMat);
                solidHullMesh.name = 'globalWireframeOutline_Hull';
                solidHullMesh.scale.setScalar(1.04);
                child.add(solidHullMesh);
              }
            }
          }
          // Ensure all outlines are visible
          child.children.filter(c => c.name.startsWith('globalWireframeOutline')).forEach(o => o.visible = true);
        } else {
          // Hide if not in wireframe mode
          if (outlines.length > 0) outlines.forEach(o => o.visible = false);
        }

        // 2. Handle Material Override (Pure White)
        // Ensure the material is actually swapped to the White Basic Material
        if (isWireframeMode) {
          if (!originalMaterialsRef.current.has(child)) {
            originalMaterialsRef.current.set(child, child.material);
          }
          // Use the super-white material ref
          child.material = wireframeMaterialRef.current;

        } else {
          // Disable wireframe: Hide outlines
          child.children.filter(c => c.name.startsWith('globalWireframeOutline')).forEach(o => o.visible = false);

          // Restore Material
          if (originalMaterialsRef.current.has(child)) {
            child.material = originalMaterialsRef.current.get(child)!;
          }

          // Restore Visibility (for parts hidden in wireframe, like Body container)
          child.visible = true;
        }

      }
    });

    return () => {
      cleanupOutlines();
    };

  }, [currentModel, isWireframeMode, gltf]);

  const modifiedMeshesRef = useRef<Set<THREE.Mesh>>(new Set());

  // Apply color customizations
  useEffect(() => {
    if (!groupRef.current) return;

    // 1. RESET PHASE
    modifiedMeshesRef.current.forEach(mesh => {
      const original = originalMaterialsRef.current.get(mesh);
      if (original) mesh.material = original;
    });
    modifiedMeshesRef.current.clear();

    // 2. APPLY PHASE
    if (colorCustomizations.length === 0 || isWireframeMode) return;

    colorCustomizations.forEach(({ partName, color }) => {
      const targetParts = [partName];

      if (partName === '__ALL__') {
        groupRef.current?.traverse((child) => {
          if (child instanceof THREE.Mesh && child.visible && !child.userData.isPrintArea) {
            if (!originalMaterialsRef.current.has(child)) originalMaterialsRef.current.set(child, child.material);

            if (child.material) {
              const applyColor = (mat: THREE.Material, indexStr: string = '') => {
                const cacheKey = `${child.uuid}_${color}${indexStr}`;
                let cachedMat = materialCacheRef.current.get(cacheKey);
                if (!cachedMat && mat instanceof THREE.MeshStandardMaterial) {
                  cachedMat = mat.clone();
                  cachedMat.color.set(color);
                  materialCacheRef.current.set(cacheKey, cachedMat);
                }
                return cachedMat || mat;
              };

              if (Array.isArray(child.material)) {
                child.material = child.material.map((mat, idx) => applyColor(mat, `_${idx}`));
              } else {
                child.material = (applyColor(child.material) as THREE.MeshStandardMaterial);
              }
              modifiedMeshesRef.current.add(child);
            }
          }
        });
      } else {
        targetParts.forEach(targetName => {
          groupRef.current?.traverse((child) => {
            if (child.name === targetName && child instanceof THREE.Mesh) {
              if (!originalMaterialsRef.current.has(child)) originalMaterialsRef.current.set(child, child.material);

              if (child.material) {
                const processMat = (mat: THREE.Material, indexStr: string = '') => {
                  const cacheKey = `${child.uuid}_${partName}_${color}${indexStr}`;
                  let cachedMat = materialCacheRef.current.get(cacheKey);

                  if (!cachedMat && mat instanceof THREE.MeshStandardMaterial) {
                    cachedMat = mat.clone();
                    cachedMat.color.set(color);

                    if (product?.id === 'square-pen' && targetName === 'Body') {
                      cachedMat.metalness = 0.0;
                      cachedMat.roughness = 0.9;
                      cachedMat.envMapIntensity = 1.0;
                      cachedMat.map = null;
                    }

                    materialCacheRef.current.set(cacheKey, cachedMat);
                  }
                  return cachedMat || mat;
                };

                if (Array.isArray(child.material)) {
                  child.material = child.material.map((mat, idx) => processMat(mat, `_${idx}`));
                } else {
                  child.material = (processMat(child.material) as THREE.MeshStandardMaterial);
                }
                modifiedMeshesRef.current.add(child);
              }
            }
          });
        });
      }
    });
  }, [colorCustomizations, product]);

  return (
    <ModelInteraction>
      <group ref={groupRef} position={[0, 0, 0]}>
        {!glbUrl && <TShirtPlaceholder />}
        <group>
          {printAreas.map((printArea, index) => (
            <PrintAreaTexture key={`${printArea.id}-${printArea.meshRef?.uuid || index}`} printArea={printArea} />
          ))}
        </group>
      </group>
    </ModelInteraction>
  );
}
